import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef } from "@angular/core";
import { Subscription, take } from "rxjs";
import { IUser } from "src/app/shared/auth/auth.model";
import { AuthenticationService } from "src/app/shared/auth/auth.service";
import { MyProfileService } from "../../services/my-profile.service";
import { LookupsService } from "../../services/lookups.service";
import { ContactInformationService } from "../../services/contact-information.service";
import { MatCardModule } from "@angular/material/card";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { OnboardingContactInformationComponent } from "./onboarding-panels/onboarding-contact-information/onboarding-contact-information.component";
import { OnboardingCompanyInformationComponent } from "./onboarding-panels/onboarding-company-information/onboarding-company-information.component";
import { OnboardingMandatoryDocumentsComponent } from "./onboarding-panels/onboarding-mandatory-documents/onboarding-mandatory-documents.component";
import { MyProfileAgreementComponent } from "../my-profile/profile-panels/my-profile-agreement/my-profile-agreement.component";
import { FinancialInformationComponent } from "../my-profile/profile-panels/financial-information/financial-information.component";
import { WireTransferComponent } from "./onboarding-panels/financial-information/wire-transfer/wire-transfer.component";
import { DirectDepositComponent } from "./onboarding-panels/financial-information/direct-deposit/direct-deposit.component";
import { MatRadioModule } from "@angular/material/radio";

// Create the agent data
export interface AgentData {
  agent: string;
  data: string;
  bold?: boolean;
}

// Populate table with some default data
const ELEMENT_DATA: AgentData[] = [
  { agent: "Company Name", data: "" },
  { agent: "Status", data: "", bold: true },
  { agent: "Certification Expiration Date", data: "" },
  { agent: "Authorized Countries of Recruitment", data: "" },
  { agent: "Director Region", data: "" },
  { agent: "Agency Since", data: "" },
];

@Component({
    selector: "app-agent-onboarding",
    imports: [
        CommonModule,
        FormsModule,
        MatCardModule,
        MatExpansionModule,
        MatButtonModule,
        MatIconModule,
        OnboardingContactInformationComponent,
        OnboardingCompanyInformationComponent,
        OnboardingMandatoryDocumentsComponent,
       
        WireTransferComponent,
        DirectDepositComponent,
        MatRadioModule,
    ],
    templateUrl: "./agent-onboarding.component.html",
    styleUrl: "./agent-onboarding.component.scss"
})
export class AgentOnboardingComponent implements OnInit, OnDestroy {
  @ViewChild(OnboardingMandatoryDocumentsComponent) mandatoryDocsComponent: OnboardingMandatoryDocumentsComponent;

  user: IUser;
  hasAccess: boolean = false;
  isAdmin: boolean = false; // We can data bind to this to pass it to child components
  displayedColumns: string[] = ["agent", "data"];
  dataSource = ELEMENT_DATA;
  agencySub: Subscription;
  countries: any = [];
  agentId: number;
  oldAgencyId: string;
  agencyId: string = "";
  authCountryArr = [];
  isLoading: boolean = true;
  contactInfoStatus: string;
  mandatoryDocsStatus: string;
  businessInfoStatus: string;
  private _contactInfo: any[] = []; // Array to store multiple contact info forms
  get ContactInfo(): any[] {
    return this._contactInfo;
  }
  set ContactInfo(value: any[]) {
    this._contactInfo = value;
    this.refreshMandatoryDocsValidation();
  }
  CompanyInfo: any;
  stepOneComplete: boolean = false;
  stepTwoComplete: boolean = false;
  contactInfoForms: number[] = [1]; // Track number of contact forms
  /** 'D' = Direct Deposit, 'W' = Wire Transfer, '' = not yet selected */
  selectedPaymentMethod: string = ''; // no default — agent must choose

  constructor(
    private authService: AuthenticationService,
    private myProfileService: MyProfileService,
    private cdr: ChangeDetectorRef,
    private lookupService: LookupsService,
    private contactInfoService: ContactInformationService
  ) {}

  async ngOnInit() {
    this.user = this.authService.getUserInfo();

    this.loadCountries();

    // Checking for roles
    if (
      this.authService.hasRole("Agent") ||
      this.authService.hasRole("Agency_Admin") ||
      this.authService.hasRole("Agency_Onboarding")
    ) {
      this.hasAccess = true;
      if (this.authService.hasRole("Agency_Admin")) {
        this.isAdmin = true;
      }
    } else {
      this.hasAccess = false;
    }

    await this.loadAgencyInfo();
    this.myProfileService.getAgencyOnboardingStatus(this.agencyId).subscribe((status: any) => {
      console.log("Agency Onboarding Status: ", status);
      if (status.step1 == 'true') {
        this.stepOneComplete = true;
      }
      if (status.step2 == 'true') {
        this.stepTwoComplete = true;
      }
    });
    // this.getOnboardingStatus();
    // this.loadTable();
  }

  loadCountries() {
    // Use the lookup service to get Countries and Codes
    this.lookupService
      .getLookupByTableAlias("country")
      .pipe(take(1))
      .subscribe((country) => {
        this.countries = country;
      });
  }

  loadTable() {
    // Load the Agency table subscribing the getAgency Service
    this.agencySub = this.myProfileService
      .getAgencyInformation(this.user.oid)
      .subscribe({
        next: (agencyInfo) => {
          if (
            !agencyInfo.authCountries ||
            agencyInfo.authCountries.length === 0
          ) {
            this.authCountryArr = ["NA"];
          } else {
            this.authCountryArr = agencyInfo.authCountries.split(",");
          }
          let authCountries = [];
          this.authCountryArr.map((countryCode) => {
            if (countryCode === "NA") {
              authCountries.push("No Authorized Countries");
            }

            for (let i = 0; i < this.countries.length; ++i) {
              if (this.countries[i].Code === countryCode) {
                authCountries.push(this.countries[i].Name);
              }
            }
          });

          // Populate the table with data
          this.agencyId = agencyInfo.agencyId;
          this.agentId = agencyInfo.agentId;
          this.oldAgencyId = agencyInfo.oldAgencyID;
          this.dataSource[0].data = agencyInfo.companyName;
          if (agencyInfo.status === "A") {
            this.dataSource[1].data = "Complete";
          } else if (agencyInfo.status === "B") {
            this.dataSource[1].data = "Probationary";
          } else {
            this.dataSource[1].data = "Undefined";
          }

          let expDate = new Date(Date.parse(agencyInfo.certExpiryDate));
          expDate.setDate(expDate.getDate() + 1);
          this.dataSource[2].data = expDate.toDateString();
          this.dataSource[3].data = authCountries.join(", ");
          this.dataSource[4].data = agencyInfo.directorRegionName;
          let sinceDate = new Date(Date.parse(agencyInfo.agencySince));
          sinceDate.setDate(sinceDate.getDate() + 1);
          this.dataSource[5].data = sinceDate.toDateString();
          this.isLoading = false;
        },
        error: (error) => {
          console.log(error.message);
        },
      });
  }

  getOnboardingStatus() {
    this.contactInfoService
      .getAgencyOnboardingStatus(this.agencyId)
      .subscribe((status: any) => {
        console.log("Agency Onboarding Status: ", status);
        this.contactInfoStatus = status.AgentContact;
        this.mandatoryDocsStatus = status.CompanyDocuments;
        this.businessInfoStatus = status.CompanyInfo;
      });
  }

  // loadAgencyInfo() {
  //   console.log("User OID in the agent onboarding: ", this.user.oid);
  //   this.contactInfoService.getContactInfoById(this.user.oid)
  //     .subscribe((agencyInfo: any) => {
  //       console.log("Agency Info: ", agencyInfo);
  //       this.agentId = agencyInfo.Agent_ID.toString();
  //       this.agencyId = agencyInfo.Agency_ID.toString();
  //     });
  // }

  async loadAgencyInfo() {
    console.log("User OID in the agent onboarding: ", this.user.oid);
    return new Promise<void>((resolve, reject) => {
      this.contactInfoService.getContactInfoById(this.user.oid).subscribe({
        next: (agencyInfo: any) => {
          if (agencyInfo && agencyInfo.Agency_ID) {
            if (agencyInfo.Agent_ID) {
              this.agentId = agencyInfo.Agent_ID.toString();
            }
            this.agencyId = agencyInfo.Agency_ID.toString();
            console.log("After assignment - agencyId: ", this.agencyId, "agentId: ", this.agentId);
          } else {
            console.error("Agency_ID is missing from response", agencyInfo);
            this.agencyId = "Error: Missing Agency ID";
          }

          resolve();
        },
        error: (error) => {
          console.error("Error loading agency info: ", error);
          this.agencyId = "Error loading data";
          reject(error);
        },
      });
    });
  }

  saveContactInfo($event: any) {
    console.log("Data in the parent component: ", $event);
    // Build array of contact forms
    if (!Array.isArray(this._contactInfo)) {
      this._contactInfo = [];
    }
    
    const contactData = $event.contactFormData || $event;
    const formIndex = $event.formIndex;
    
    // Use formIndex to determine if we're updating or adding
    if (formIndex !== undefined && this._contactInfo[formIndex]) {
      // Update existing contact at this index
      console.log(`Updating contact at index ${formIndex}`);
      this._contactInfo[formIndex] = contactData;
    } else if (formIndex !== undefined && formIndex === this._contactInfo.length) {
      // Add new contact at the next index
      console.log(`Adding new contact at index ${formIndex}`);
      this._contactInfo.push(contactData);
    } else {
      // Fallback: add to array
      console.log("Adding contact to array (fallback)");
      this._contactInfo.push(contactData);
    }
    
    console.log("ContactInfo array updated, total forms:", this._contactInfo.length, "All forms:", this._contactInfo);
    this.logDebugData();
    
    // Trigger change detection in mandatory documents component to re-evaluate hasSigningOfficer()
    this.refreshMandatoryDocsValidation();
  }

  saveCompanyInfo($event: any) {
    console.log("Data in the parent component: ", $event);
    this.CompanyInfo = $event;
    this.logDebugData();
    this.refreshMandatoryDocsValidation();
  }

  logDebugData() {
    console.log("=== DEBUG: Current Data State ===");
    console.log("ContactInfo Array:", JSON.stringify(this.ContactInfo, null, 2));
    console.log("CompanyInfo:", JSON.stringify(this.CompanyInfo, null, 2));
    console.log("=== END DEBUG ===");
  }

  refreshMandatoryDocsValidation() {
    if (this.mandatoryDocsComponent) {
      this.mandatoryDocsComponent.refreshSigningOfficerCheck();
    }
  }

  onFinancialInfoSubmitted(): void {
    // Brief delay so the child's confirmation message is visible, then switch to the full "submitted" screen
    setTimeout(() => {
      this.stepTwoComplete = true;
    }, 2000);
  }

  addContactInfoForm() {
    const newFormId = Math.max(...this.contactInfoForms) + 1;
    this.contactInfoForms.push(newFormId);
  }

  ngOnDestroy() {
    if (this.agencySub) this.agencySub.unsubscribe();
  }
}
